import { Component, Input, OnInit, inject } from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";
import { VaultFileView } from "@bitwarden/web-vault/app/models/view/vault-file/vault-file.view";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
// Global, unencapsulated stylesheet: injected via this side-effect import, not Angular's
// styleUrls/styleUrl, so it isn't scoped to this component.
import "./edit-cabinet.component.scss";
import { TransactionStatusEnum } from "@bitwarden/web-vault/app/models/enum/transactionType";
import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import { ActionButton } from "@bitwarden/web-vault/app/components/buttons/gloss-button/actionButton";

@Component({
  selector: "app-edit-cabinet-upload",
  templateUrl: "./edit-cabinet.component.html",
})
export class EditCabinetComponent implements OnInit {
  @Input() file: VaultFileView;
  @Input() accounts: AccountView[] = [];
  private transactionStoreService: TransactionStoreService = inject(TransactionStoreService);
  private confirmationDialogService: ConfirmationDialogService = inject(ConfirmationDialogService);
  private readonly i18n = inject(I18nService);
  private userStoreService = inject(UserStoreService);
  private dialogRef = inject(MatDialogRef<EditCabinetComponent>);
  private accountView: AccountView[] = [];
  showLinkAccount = false;
  selectedAccount: AccountView;
  accountOptions: string[] = [];
  accountCurrency: string;
  openingBalanceInput: number;
  closingBalanceInput: number;

  protected readonly draftOptions = new ActionButton({
    text: this.i18n.t("saveAndAddLater"),
    class: "neutral",
    onClick: () => this.saveDraft(),
  });
  protected readonly saveOptions = new ActionButton({
    text: this.i18n.t("addTransaction"),
    class: "primary",
    onClick: () => this.saveToDB(),
  });

  get linkedAccounts() {
    return this.accounts.map((acc) => ({
      name: acc.name,
      currency: acc.currency,
      id: acc.id,
      openingBalance: this.file?.openingBalances,
      closingBalance: this.file?.closingBalances,
    }));
  }

  openingBalance(accountId: string) {
    const dateStr = this.file.statementFromDate;
    return this.transactionStoreService.enrichTransaction
      .collection()
      .find(
        (tx) =>
          tx.definition === TransactionStatusEnum.opening &&
          tx.accountLink.id === accountId &&
          this.isSameDate(tx.glossPostDate.date, dateStr),
      );
  }

  closingBalance(accountId: string) {
    const dateStr = this.file.statementToDate;
    return this.transactionStoreService.enrichTransaction
      .collection()
      .find(
        (tx) =>
          tx.definition === TransactionStatusEnum.closing &&
          tx.accountLink.id === accountId &&
          this.isSameDate(tx.glossPostDate.date, dateStr),
      );
  }

  async ngOnInit(): Promise<void> {
    this.accountView = this.userStoreService.enrichAccounts.collection();
    this.accountOptions = this.accountView.map((acc) => acc.displayName);

    const draftString = localStorage.getItem(`${this.file.id}`);
    if (draftString) {
      const draft = JSON.parse(draftString);

      if (draft.selectedAccount) {
        this.selectedAccount = this.accountView.find((acc) => acc.id === draft.selectedAccount.id);
        this.accountCurrency = this.selectedAccount?.currency;
      }
      this.openingBalanceInput = draft.openingBalanceInput;
      this.closingBalanceInput = draft.closingBalanceInput;
      this.showLinkAccount = !!draft.showLinkAccount;
    }
  }

  removeLinkAccount(): void {
    this.selectedAccount = null;
    this.accountCurrency = null;
    this.openingBalanceInput = null;
    this.closingBalanceInput = null;
    this.showLinkAccount = false;
  }

  isSameDate(date1Str: string, date2Str: string): boolean {
    const d1 = new Date(date1Str);
    const d2 = new Date(date2Str);
    return (
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate()
    );
  }

  private clearInput(): void {
    this.selectedAccount = null;
    this.accountCurrency = null;
    this.openingBalanceInput = null;
    this.closingBalanceInput = null;
    localStorage.removeItem(`${this.file.id}`);
  }

  private shouldShowLinkAccount(): boolean {
    if (!this.showLinkAccount) {
      this.showLinkAccount = true;
      return true;
    }
    return false;
  }

  private isAccountInputInvalid(): boolean {
    return (
      !this.selectedAccount || this.openingBalanceInput == null || this.closingBalanceInput == null
    );
  }

  private async updateOpeningClosingBalances(): Promise<void> {
    const fromDateStr = this.file.statementFromDate;
    const toDateStr = this.file.statementToDate;
    const accountId = this.selectedAccount.id;

    const cloneTransaction = (definition: TransactionStatusEnum, dateStr: string) =>
      this.transactionStoreService.enrichTransaction
        .collection()
        .find(
          (tx) =>
            tx.definition === definition &&
            tx.accountLink.id === accountId &&
            this.isSameDate(tx.glossPostDate.date, dateStr),
        )
        .clone();

    const openingTransaction = cloneTransaction(TransactionStatusEnum.opening, fromDateStr);
    const closingTransaction = cloneTransaction(TransactionStatusEnum.closing, toDateStr);

    openingTransaction.bankImportedBalance = this.openingBalanceInput;
    closingTransaction.bankImportedBalance = this.closingBalanceInput;

    await this.transactionStoreService.transaction.save(openingTransaction);
    await this.transactionStoreService.transaction.save(closingTransaction);
  }

  async linkAnotherAccount(): Promise<void> {
    if (this.shouldShowLinkAccount()) {
      return;
    }
    if (this.isAccountInputInvalid()) {
      return;
    }

    try {
      const cabinet = this.file.clone();
      const accountId = this.selectedAccount.id;

      const alreadyLinked = cabinet.accountIds.includes(accountId);
      if (!alreadyLinked) {
        cabinet.accountIds.push(accountId);
        await this.userStoreService.vaultFiles.save(cabinet);
        this.file.accountIds = [...cabinet.accountIds];
      }

      await this.updateOpeningClosingBalances();

      this.confirmationDialogService.notify(
        NotificationEnum.success,
        "succeeded",
        "updatedSuccessfully",
      );
    } catch (e) {
      this.confirmationDialogService.notify(
        NotificationEnum.error,
        "errorOccurred",
        "somethingWentWrong",
      );
    }

    this.clearInput();
    this.dialogRef.close();
  }

  accountSelected(accountName: string): void {
    const account = this.accountView.find((acc) => acc.name === accountName);
    if (account) {
      this.selectedAccount = account;
      this.accountCurrency = account.currency;
    }
  }

  saveDraft(): void {
    const draft = {
      selectedAccount: this.selectedAccount,
      openingBalanceInput: this.openingBalanceInput,
      closingBalanceInput: this.closingBalanceInput,
      showLinkAccount: this.showLinkAccount,
    };
    localStorage.setItem(`${this.file.id}`, JSON.stringify(draft));
    this.dialogRef.close();
  }

  async saveToDB(): Promise<void> {}
}
